import Button from "../../Button";
import constants from "../../../constants";
import CcSecQnsUtils from "./CcSecQnsUtils";
import { useParams, useLocation } from "react-router-dom";
import ccSecQnsUtils from "./ccSecQnsUtils.json";
import Table from "../../CommonComponents/Table";
import Search from "../../CommonComponents/Search";
import Tooltip from "../../CommonComponents/Tooltip";
import AlertContext from "../../../context/AlertContext";
import IconButton from "../../CommonComponents/IconButton";
import useAxiosPrivate from "../../../Hooks/useAxiosPrivate";
import Loading from "../../Notification-Loading/Loading/Loading";
import { getDataFromStorage, MANAGEMENTROLES } from "../../../util";
import { useDebouncedValue } from "../../../Hooks/useDebounceValue";
import CodingViewHooks from "../../../Hooks/CodingPage/CodingViewHooks";
import useCommonFunctionHooks from "../../../Hooks/useCommonFunctionHooks";

import React, {
  useRef,
  Fragment,
  useState,
  useEffect,
  useContext,
} from "react";
import GraphForAttempts from "../MockTestPage/GraphForAttempts";

const calculatePageSize = () => {
  const viewportHeight = window.innerHeight;
  const itemHeight = 48;

  return Math.floor(viewportHeight / itemHeight);
};

const CcSecQuestions = () => {
  const Alert = useContext(AlertContext);

  const {
    isLoaded,
    setTopic,
    navigate,
    setStatus,
    setIsLoaded,
    setTestData,
    setShowNotify,
    setCodingTestData,
    setShowInstruction,
  } = useContext(AlertContext);

  const axios = useAxiosPrivate();
  const useCommonFunctionHks = useCommonFunctionHooks();

  const { viewCode } = CodingViewHooks();

  const { idHash, username, secid, sname } = useParams();
  const { pathname } = useLocation();

  console.log("idHash, username, secid, sname:", idHash, username, secid, sname);

  const tableRef = useRef(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => calculatePageSize());
  const [hasMore, setHasMore] = useState(true);
  const [searchValue, setSearchValue] = useState("");
  const [activeCard, setActiveCard] = useState(null);
  const [showFilter, setShowFilter] = useState(false);
  const [secQuestions, setSecQuestions] = useState([]);
  const [openedRowId, setOpenedRowId] = useState(null);
  const [qnFilterCount, setQnFilterCount] = useState({});
  const [complexityId, setComplexityId] = useState(null);

  const [qnsFilterValue, setQnsFilterValue] = useState({
    qns: true,
    attem: false,
    solv: false,
    first: false,
  });

  const debouncedSearchValue = useDebouncedValue(searchValue, 2000);

  const getQueryString = (obj) => {
    const trueKey = Object.keys(obj).find((key) => obj[key] === true);

    return trueKey ? `${trueKey}=true` : "";
  };

  useEffect(() => {
    const handleResize = () => {
      setPageSize(calculatePageSize());
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const addAllSolvedComplexityWise = (solved) => {
    const total = solved.reduce((acc, curr) => {
      return acc + curr.count;
    }, 0);
    // console.log("total:", total);
    return total;
  };

  useEffect(() => {
    const fetchSecQuestions = async () => {
      if (!hasMore) return;

      setIsLoaded(true);

      try {
        const response = await axios.get(
          pathname.includes("/u/") ?
            `node/public/u/${idHash}/${username}/cc/section/${sname}/question?page=${page}&limit=${pageSize}&cid=${complexityId}&name=${debouncedSearchValue}&${getQueryString(
              qnsFilterValue
            )}`
            : `node/learner/cc/section/${secid}/question?page=${page}&limit=${pageSize}&cid=${complexityId}&name=${debouncedSearchValue}&${getQueryString(
              qnsFilterValue
            )}`,
          useCommonFunctionHks.getHeaderDetailsForAxios()
        );

        if (response.data.resultCode === constants.RESULT_STATUS.SUCCESS) {
          const data = response.data.data.questions || [];
          const totalCount = response.data.data.totalCount || 0;

          setSecQuestions((prev) => {
            const questions = page === 1 ? data : [...prev, ...data];
            // console.log("questions:", questions, "totalCount:", totalCount);
            if (totalCount <= questions.length) setHasMore(false);
            return questions.map((item, index) => ({
              sno: index + 1,
              ...item,
            }));
          });

          setQnFilterCount({
            categoryCount: response.data.data.solvedCount,
            totalCount: totalCount ?? 0,
            firstCount: response.data.data.firstCount ?? 0,
            attemptCount: response.data.data.attemptCount ?? 0,
            solvedCount: {
              totalCount: response.data.data.solvedCount.reduce(
                (accumulator, currentValue) =>
                  accumulator + currentValue.count ?? 0,
                0
              ),
              categoryCount: response.data.data.solvedCount,
            },
          });
        } else {
          setShowNotify({
            show: true,
            title: "Error!",
            msg: "Something went wrong. Please try again later or contact support.",
          });
        }
      } catch (error) {
        useCommonFunctionHks.getCatchBlockDetails(error);
        setHasMore(false);
      } finally {
        setIsLoaded(false);
      }
    };

    // Fetch when either idHash (public profile) or secid (protected route) is available
    if (idHash || Number.isInteger(Number(secid))) {
      fetchSecQuestions();
    }
  }, [
    page,
    idHash,
    username,
    secid,
    sname,
    axios,
    hasMore,
    pageSize,
    setIsLoaded,
    complexityId,
    setShowNotify,
    qnsFilterValue,
    debouncedSearchValue,
  ]);

  useEffect(() => {
    setPage(1);
    setHasMore(true);
    setSecQuestions([]);
  }, [complexityId, debouncedSearchValue, qnsFilterValue]);

  const handleScroll = () => {
    if (!tableRef.current || isLoaded || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = tableRef.current;

    if (scrollTop + clientHeight >= scrollHeight - 10) {
      setPage((prev) => prev + 1);
    }
  };

  const prepareTestData = (data, overrides = {}) => {
    return {
      ...data,
      ...overrides,
      single: true,
      instruction: null,
      testmode: constants.MODE.PRACTICE_MODE,
      status: constants.QUESTION_STATUS.ASSIGNED,
      proctored: constants.PROCTORING_MODE.NONE,
      testtype: constants.libTypeIds.CODING_CHALLENGE,
      configdata: {
        languageid: data.languageid,
      },
    };
  };

  const takeTest = (event, data) => {
    
    event.preventDefault();
    
    const testData = prepareTestData(data);
    setTestData(testData);
    Alert.setCurrentStep(0);
    setCodingTestData(testData);
    setShowInstruction(true);
    setStatus(constants.QUESTION_STATUS.ASSIGNED);
    setTopic({ id: testData.secid, name: testData.secname });
    navigate("/instruction");
  };
  
  const viewTest = (data) => {
    const testData = prepareTestData(data, {
      qndata: [
        {
          status: constants.QUESTION_STATUS.SUBMITTED,
        },
      ],
    });

    setTestData(testData);
    setCodingTestData(testData);
    setTopic({ id: testData.secid, name: testData.secname });
    viewCode(testData);
  };

  const columns = [
    {
      key: "sno",
      title: "S.No",
      className: "serialNo",
    },
    {
      key: "title",
      title: "Question",
      className: "title",
    },
    {
      key: "complexityid",
      title: "Difficulty",
      className: "difficulty",
    },
  ];

  let data = [];

  const getColor = (score, targetscore) => {
    // console.log("core:",score,"targte:",targetscore,"score/targetscore:",score/targetscore,"targetscore/2:",targetscore/2);
    return score > targetscore / 2 ? "green" : "red";
  };
  const renderCustomRow = (row, rowIndex, columns) => {
    const attempts = JSON.parse(row?.attempteddata ?? "[]");

    const graphData = attempts.map((attempt, index) => ({
      attempt: `${index + 1}`,
      score: attempt.actualscore,
      targetscore: row.targetscore,
    }));

    const slicedGraphData =
      graphData.length > 4 ? graphData.slice(-4) : graphData;
    return (
      <Fragment key={row.questionid}>
        <tr
          key={rowIndex}
          className={`tableBodyRow ${openedRowId === row.sno ? "opened" : ""}`}
          id={`${rowIndex % 2 === 0 ? "even" : "odd"}`}
        >
          <td className={`tableData sno serialNo`}>{row.sno}</td>
          <td className={`tableData title`}>
            <div className="ccSecQnsTableBodyTitleAndStatusSec">
              <Tooltip placement="bottom" title={row.title}>
                <p className="ccSecQnsTableBodyTitle">{row.title}</p>
              </Tooltip>
              {row.lccid && (
                <Tooltip
                  position="bottom"
                  title={
                    row.actualscore === row.targetscore
                      ? "Solved"
                      : row.attempteddata?.length
                        ? "Attempted"
                        : ""
                  }
                  isIcon={true}
                >
                  <div
                    className={`ccSecQnsTableBodyStatus ${row.actualscore === row.targetscore
                      ? "solved"
                      : row.attempteddata?.length
                        ? "attempted"
                        : ""
                      }`}
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M10 3L4.5 8.5L2 6"
                        stroke="#667085"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </Tooltip>
              )}
            </div>
          </td>
          <td className={`tableData difficulty`}>
            <div className="ccSecQnsTableBodyDifficultySec">
              <p className={row?.cname?.toLowerCase()}>
                {row.complexityid === constants.complexityid.HARD
                  ? "Hard"
                  : row.cname}
              </p>
              <div
                className={`ccSecQnsTableBodyDifficultyUtils ${row.sno === openedRowId ? "opened" : ""
                  }`}
              >
                {(pathname.includes("/main/cc")) && (
                  <Button
                    size="sm"
                    hierarchy={{ type: "secondaryGrey", buttonText: "Solve" }}
                    onClick={(event) => takeTest(event, row)}
                  />
                )}
                {JSON.parse(row?.attempteddata ?? "[]").length ? (
                  <IconButton
                    children={
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M5 7.5L10 12.5L15 7.5"
                          stroke="#667085"
                          strokeWidth="1.66667"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    }
                    size="sm"
                    hierarchy="tertiaryGrey"
                    onClick={() =>
                      setOpenedRowId(openedRowId === row.sno ? null : row.sno)
                    }
                  />
                ) : null}
              </div>
            </div>
          </td>
        </tr>
        {openedRowId === row.sno && (
          <tr
            className={`tableBodyRow expandRow`}
            id={`${rowIndex % 2 === 0 ? "even" : "odd"}`}
          >
            <td className="tableData" colSpan={columns.length}>
              {JSON.parse(row?.attempteddata ?? "[]")?.length ? (
                <div
                  className={`ccSecQnsTableBodyAttemptSec ${row.sno === openedRowId ? "opened" : ""
                    }`}
                >
                  <div className="ccSecQnsTableBodyAttemptContainer">
                    {JSON.parse(row?.attempteddata ?? "[]")?.map(
                      (attempt, index) => {
                        return (
                          <div
                            className={`ccSecQnsTableBodyAttempt ${pathname.includes("/u/") ? "viewOnly" : "clickable"}`}
                            key={attempt.attempt_id}
                            onClick={() => {
                              if (pathname.includes("/u/")) return;
                              viewTest({ ...row, lccid: attempt.attempt_id, questionid: attempt.question_id })
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "2.89px",
                              }}
                            >
                              <p className="ccSecQnsTableBodyAttemptCount">
                                Attempt {index + 1}
                              </p>
                              <p className="ccSecQnsTableBodyAttemptCreated">
                                {attempt.timestamp.split(" ")[0]}
                              </p>
                            </div>
                            <p
                              className={`ccSecQnsTableBodyAttemptScoreSec ${getColor(
                                attempt.actualscore,
                                row.targetscore
                              )}`}
                            >
                              <span className="ccSecQnsTableBodyAttemptScore">
                                {attempt.actualscore}/{row.targetscore}
                              </span>
                            </p>
                          </div>
                        );
                      }
                    )}
                  </div>

                  {attempts.length > 2 && (
                    <div className="ccSecQnsTableBodyAttemptGraph">
                      <GraphForAttempts
                        attemptedData={slicedGraphData}
                        page={"ccpage"}
                      />
                    </div>
                  )}
                </div>
              ) : null}
            </td>
          </tr>
        )}
      </Fragment>
    );
  };

  const onClickFilter = (value) => {
    setQnsFilterValue((prev) =>
      Object.keys(prev).reduce((acc, key) => {
        acc[key] = key === value;
        return acc;
      }, {})
    );
  };

  const onClickBack = () => {
    navigate(`/u/${idHash}/${username}`);
  };

  console.log("qnsFilterValue:", qnsFilterValue);

  return (
    <div className="ccSecQnsContainer ccContainer">
      <div className="ccSecQnsContainerSec">
        <div className="ccSecQnsHeadUtilsContainer">
          {(idHash || pathname.includes("/u/")) && (
            <div className={`create-test-back-btn-wrapper`} onClick={onClickBack}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" data-supported-dps="16x16" fill="#667085" width="12" height="12" focusable="false">
                <path d="M1.29 8.71a1 1 0 010-1.41L6.59 2l1.29 1.29L4.17 7H14v2H4.17l3.71 3.71L6.59 14z"></path>
              </svg>
              <p className="create-test-back-btn-text">Back</p>
            </div>
          )}
          <div className="ccSecQnsHeaderContainer">
            <div className="ccSecQnsHeaderLeftSec">
              {/* <div
                className="ccSecQnsHeader"
                onClick={() => Alert.navigate("/main/cc")}
              >
                Coding Challenges
              </div>
              <svg
                width="8"
                height="10"
                fill="none"
                viewBox="0 0 8 10"
                style={{ alignSelf: "center" }}
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill="#667085"
                  d="M0.24 0.24H3.12L7.72 4.78L3.12 9.32H0.24L4.86 4.78L0.24 0.24Z"
                />
              </svg> */}

              <div className="ccSecQnsHeaderSecName">
                <div
                  className="ccSecQnsHeaderSecName-Codingchallenges"
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    if (pathname.includes("/u/")) navigate(`/u/${idHash}/${username}`)
                    else navigate("/main/cc")
                  }}
                >{idHash ? `Profile` : `Coding Challenges`}</div>
                <svg
                  width="6"
                  height="10"
                  viewBox="0 0 6 10"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0.800049 8.80005L4.80005 4.80005L0.800049 0.800049"
                    stroke="#667085"
                    stroke-width="1.6"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>

                <span> {sessionStorage.getItem("ccSecName") ?? ""}</span>
                {/* {sessionStorage.getItem("ccSecName")} */}
              </div>
            </div>
            <div className="ccSecQnsHeaderRightSec">
              <div
                className="ccSecQnsHeaderFilter"
                onMouseOver={() => setShowFilter(true)}
                onMouseLeave={() => setShowFilter(false)}
              >
                <IconButton
                  hierarchy={showFilter ? "secondaryGrey" : "tertiaryGrey"}
                >
                  <svg
                    width="20"
                    height="20"
                    fill="none"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      stroke="#667085"
                      strokeWidth="1.66667"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 10H15M2.5 5H17.5M7.5 15H12.5"
                    />
                  </svg>
                </IconButton>
                {showFilter && (
                  <DifficultyFilter
                    setComplexityId={setComplexityId}
                    complexityId={complexityId}
                  />
                )}
              </div>
              <Search
                value={searchValue}
                onChange={(event) => setSearchValue(event.target.value)}
              />
            </div>
          </div>
          <div className="ccSecQnsUtilsContainer">
            {ccSecQnsUtils.map((item) => (
              <CcSecQnsUtils
                item={item}
                key={item.key}
                count={qnFilterCount[item.key]}
                onClick={onClickFilter}
                activeCard={activeCard}
                setActiveCard={setActiveCard}
              />
            ))}
          </div>
        </div>
        <div
          ref={tableRef}
          onScroll={handleScroll}
          className="ccSecQnsTableContainer"
        >
          <Table
            columns={columns}
            data={secQuestions}
            renderRow={renderCustomRow}
          />
        </div>
      </div>
      {isLoaded && MANAGEMENTROLES.includes(getDataFromStorage("role")) && (
        <Loading />
      )}
    </div>
  );
};

export default CcSecQuestions;

const DifficultyFilter = ({ setComplexityId, complexityId }) => {
  const renderTickIcon = (isSelected) =>
    isSelected && (
      <svg
        className="inputDropdownMenuItemIcon tickIcon"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M16.6668 5L7.50016 14.1667L3.3335 10"
          stroke="#F55533"
          strokeWidth="1.66667"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );

  return (
    <ul className="difficultyFilterContainer">
      <li
        className={`difficultyItem all ${complexityId === null ? "selected" : ""
          }`}
        onClick={() => setComplexityId(null)}
      >
        All
        {renderTickIcon(complexityId === null)}
      </li>
      {Object.entries(constants.complexityid).map(([label, id]) => (
        <li
          key={id}
          className={`difficultyItem ${label.toLowerCase()} ${complexityId === id ? "selected" : ""
            }`}
          onClick={() => setComplexityId(id)}
        >
          {label.toLowerCase()}
          {renderTickIcon(complexityId === id)}
        </li>
      ))}
    </ul>
  );
};
